import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, Package, MapPin, Clock, Star, AlertTriangle,
  CheckCircle, ChevronRight, Users, Layers, Award, Shield,
  ArrowRight, Loader2, Search, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SVC_BADGE = {
  PRIORITY: "bg-red-100 text-red-700 border-red-200",
  EXPRESS:  "bg-orange-100 text-orange-700 border-orange-200",
  STANDARD: "bg-blue-100 text-blue-700 border-blue-200",
};

const OVERRIDE_REASONS = [
  "Bus cancelled / service not running today",
  "Cut-off time missed for this connection",
  "Capacity full on this service today",
  "Customer requested specific carrier",
  "Better connection available (faster or cheaper)",
  "Other",
];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ─── Shipment Card (left sidebar) ─── */
function ShipmentCard({ s, selected, onClick }) {
  const svc = s.service_level || "STANDARD";
  return (
    <button
      onClick={onClick}
      data-testid={`queue-card-${s.id}`}
      className={`w-full text-left border rounded-lg p-3 transition-all hover:shadow-sm ${
        selected ? "border-l-4 border-l-indigo-600 bg-indigo-50/50 border-indigo-200" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-xs text-slate-500">{s.order_id || s.tracking_id || s.id?.slice(-8)}</span>
        <Badge className={`text-[9px] px-1.5 py-0 border ${SVC_BADGE[svc] || SVC_BADGE.STANDARD}`}>{svc}</Badge>
      </div>
      <div className="flex items-center gap-1 text-sm">
        <span className="font-medium text-slate-800 truncate">{s.customer_name}</span>
        <Badge className="text-[9px] bg-slate-100 text-slate-600">{s.order_type || "B2C"}</Badge>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
        <MapPin className="w-3 h-3" />
        <span className="truncate">{s.origin_city || "—"}</span>
        <ArrowRight className="w-3 h-3 flex-shrink-0" />
        <span className="truncate font-medium text-slate-700">{s.dest_city || "—"}</span>
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-400">
        <span>{s.chargeable_weight_kg || s.package?.weight || 0}kg</span>
        <span className="font-medium text-slate-600">{'\u20B9'}{s.customer_price || s.price || 0}</span>
        <span className={s.is_partner_serviceable ? "text-emerald-600" : "text-red-500"}>
          {s.is_partner_serviceable ? "Partner" : "No Partner"}
        </span>
        <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{timeAgo(s.created_at)}</span>
      </div>
      {s.consolidation_count > 0 && (
        <div className="mt-1 text-[10px] text-amber-600 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
          {s.consolidation_count} more to {s.dest_city}
        </div>
      )}
    </button>
  );
}

/* ─── Connection Option Card ─── */
function OptionCard({ opt, onAssign }) {
  const isRec = opt.is_recommended;
  return (
    <div
      data-testid={`option-${opt.carrier}`}
      className={`border rounded-lg p-4 transition-shadow hover:shadow-md ${
        isRec ? "border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isRec ? (
            <Badge className="text-[10px] bg-indigo-600 text-white"><Award className="w-3 h-3 mr-0.5" />RECOMMENDED</Badge>
          ) : (
            <Badge className="text-[10px] bg-slate-200 text-slate-600">OPTION {opt.rank}</Badge>
          )}
        </div>
        <span className="text-xs text-slate-500">Est: {opt.est_days}</span>
      </div>
      <h4 className="font-bold text-slate-800 text-sm">{opt.display}</h4>
      <p className="text-xs text-slate-500 mt-0.5">{opt.reason}</p>
      <div className="flex items-center gap-4 mt-2 text-xs">
        <div><span className="text-slate-400">Our Cost:</span> <span className="font-bold text-red-600">{'\u20B9'}{opt.our_cost}</span></div>
        <div><span className="text-slate-400">Margin:</span> <span className={`font-bold ${opt.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{'\u20B9'}{opt.margin} ({opt.margin_percent}%)</span></div>
      </div>
      <Button size="sm" onClick={() => onAssign(opt)} className={`mt-3 w-full text-xs ${isRec ? "bg-indigo-600 hover:bg-indigo-700" : "bg-slate-700 hover:bg-slate-800"}`}>
        Assign <ChevronRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN ASSIGNMENT CONSOLE
   ═══════════════════════════════════════════════════════════════ */
export default function AssignmentConsole() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [svcFilter, setSvcFilter] = useState("ALL");
  const [selectedId, setSelectedId] = useState(null);
  const [options, setOptions] = useState([]);
  const [enriched, setEnriched] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Assign dialog
  const [assignOpt, setAssignOpt] = useState(null);
  const [carrierAwb, setCarrierAwb] = useState("");

  // Override modal
  const [overrideOpt, setOverrideOpt] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideText, setOverrideText] = useState("");
  const [overrideAwb, setOverrideAwb] = useState("");

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 10000); // 10s safety timeout
    try {
      let r = await fetch(`${API}/api/v2/assignments/queue`, {
        headers, signal: ctrl.signal,
      });
      if (!r.ok) {
        // Fallback to legacy shipments endpoint
        r = await fetch(`${API}/api/shipments?status=booked`, {
          headers, signal: ctrl.signal,
        });
      }
      clearTimeout(timeoutId);
      if (!r.ok) {
        setLoadError(`Server returned ${r.status}`);
        setQueue([]);
      } else {
        const j = await r.json();
        const data = Array.isArray(j) ? j : (j.items || j.shipments || []);
        setQueue(data);
      }
    } catch (e) {
      clearTimeout(timeoutId);
      setLoadError(e?.name === "AbortError"
        ? "Request timed out after 10 seconds"
        : "Unable to load assignment queue. Check connection.");
      setQueue([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const selectShipment = async (s) => {
    setSelectedId(s.id);
    setOptions([]);
    setEnriched(null);
    setOptionsLoading(true);
    try {
      const r = await fetch(`${API}/api/v2/assignments/recommend/${s.id}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setOptions(d.options || []);
        setEnriched(d.enriched_shipment_data || s);
      }
    } catch { /* ignore */ }
    setOptionsLoading(false);
  };

  const handleAssignClick = (opt) => {
    if (opt.is_recommended) {
      setAssignOpt(opt);
      setCarrierAwb("");
    } else {
      setOverrideOpt(opt);
      setOverrideReason("");
      setOverrideText("");
      setOverrideAwb("");
    }
  };

  const confirmAssign = async () => {
    const isOwn = assignOpt.carrier.startsWith("OWN");
    const isDelhivery = assignOpt.carrier.startsWith("DELHIVERY");
    if (!isOwn && !isDelhivery && !carrierAwb.trim()) return toast.error("Enter carrier AWB number");
    try {
      const r = await fetch(`${API}/api/v2/assignments/${selectedId}/assign`, {
        method: "POST", headers,
        body: JSON.stringify({
          carrier: assignOpt.carrier,
          network_type: isOwn ? "OWN" : "3PL",
          carrier_awb: (isOwn || isDelhivery) ? "" : carrierAwb,
          is_recommended: true,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Assigned to ${assignOpt.display} | AWB: ${d.awb || "pending"}`);
        setAssignOpt(null);
        setSelectedId(null);
        setOptions([]);
        setEnriched(null);
        fetchQueue();
      } else {
        const e = await r.json();
        toast.error(e.detail || "Assignment failed");
      }
    } catch (e) { toast.error("Error: " + e.message); }
  };

  const confirmOverride = async () => {
    if (!overrideReason) return toast.error("Select an override reason");
    if (overrideReason === "Other" && !overrideText.trim()) return toast.error("Enter override reason details");
    const isOwn = overrideOpt.carrier.startsWith("OWN");
    if (!isOwn && !overrideAwb.trim()) return toast.error("Enter carrier AWB number");
    try {
      const r = await fetch(`${API}/api/v2/assignments/${selectedId}/assign`, {
        method: "POST", headers,
        body: JSON.stringify({
          carrier: overrideOpt.carrier,
          network_type: isOwn ? "OWN" : "3PL",
          carrier_awb: isOwn ? "" : overrideAwb,
          is_recommended: false,
          override_reason: overrideReason,
          override_reason_text: overrideReason === "Other" ? overrideText : "",
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Override assigned to ${overrideOpt.display} | AWB: ${d.awb || "pending"}`);
        setOverrideOpt(null);
        setSelectedId(null);
        setOptions([]);
        setEnriched(null);
        fetchQueue();
      } else {
        const e = await r.json();
        toast.error(e.detail || "Assignment failed");
      }
    } catch (e) { toast.error("Error: " + e.message); }
  };

  // Filtered queue
  const filtered = queue.filter(s => {
    if (svcFilter !== "ALL" && (s.service_level || "STANDARD") !== svcFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(s.order_id || "").toLowerCase().includes(q) &&
          !(s.tracking_id || "").toLowerCase().includes(q) &&
          !(s.customer_name || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const selected = enriched || queue.find(s => s.id === selectedId);

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-80px)]" data-testid="assignment-console">
        {/* LEFT: Queue */}
        <div className="w-[35%] border-r flex flex-col bg-slate-50/50">
          <div className="p-3 border-b bg-white">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-indigo-600" /> Assignment Queue
              </h2>
              <Badge className="bg-indigo-100 text-indigo-700 text-xs">{queue.length}</Badge>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search AWB or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="queue-search" />
            </div>
            <div className="flex gap-1">
              {["ALL", "PRIORITY", "EXPRESS", "STANDARD"].map(f => (
                <button key={f} onClick={() => setSvcFilter(f)}
                  className={`px-2 py-1 rounded text-[10px] font-bold ${svcFilter === f ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                  {f === "ALL" ? `All (${queue.length})` : f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : loadError ? (
              <div className="text-center py-10 px-4" data-testid="assignment-error">
                <div className="text-sm text-red-600 mb-3">{loadError}</div>
                <button onClick={fetchQueue} data-testid="assignment-retry-btn"
                  className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded">Retry</button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-xs text-slate-400" data-testid="assignment-empty">
                No shipments awaiting assignment
              </div>
            ) : (
              filtered.map(s => (
                <ShipmentCard key={s.id} s={s} selected={selectedId === s.id} onClick={() => selectShipment(s)} />
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Detail + Options */}
        <div className="w-[65%] overflow-y-auto">
          {!selectedId ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Package className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm font-medium">Select a shipment from the queue to assign</p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* SUB-SECTION A: Shipment Facts */}
              {selected && (
                <div className="border rounded-xl p-4 bg-white" data-testid="shipment-facts">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Shipment Details</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                    <div><span className="text-slate-400">AWB:</span> <span className="font-mono font-bold">{selected.awb || "Pending assignment"}</span></div>
                    <div><span className="text-slate-400">Order ID:</span> <span className="font-mono">{selected.order_id || selected.tracking_id}</span></div>
                    <div><span className="text-slate-400">Customer:</span> <span className="font-medium">{selected.customer_name}</span> <Badge className="text-[9px] bg-slate-100 ml-1">{selected.order_type || "B2C"}</Badge></div>
                    <div><span className="text-slate-400">Service:</span> <Badge className={`text-[9px] ml-1 ${SVC_BADGE[selected.service_level || "STANDARD"]}`}>{selected.service_level || "STANDARD"}</Badge></div>
                    <div><span className="text-slate-400">Origin:</span> <span className="font-medium">{selected.origin_pincode} {selected.origin_city}</span></div>
                    <div><span className="text-slate-400">Destination:</span> <span className="font-medium">{selected.dest_pincode} {selected.dest_city} ({selected.dest_state})</span></div>
                    <div><span className="text-slate-400">Actual:</span> {selected.package?.weight || 0}kg <span className="text-slate-300 mx-1">|</span> <span className="text-slate-400">Vol:</span> {selected.volumetric_weight_kg || "—"}kg</div>
                    <div><span className="text-slate-400">Chargeable:</span> <span className="font-bold">{selected.chargeable_weight_kg || selected.package?.weight || 0}kg</span></div>
                    <div className="col-span-2 bg-slate-50 rounded px-2 py-1 mt-1">
                      <span className="text-slate-400">Customer Price:</span> <span className="font-bold">{'\u20B9'}{selected.customer_price || selected.price || 0}</span> <span className="text-slate-400">(locked)</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {selected.is_partner_serviceable ? (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-0.5" />Partner Serviceable</Badge>
                    ) : (
                      <Badge className="text-[10px] bg-red-100 text-red-700"><X className="w-3 h-3 mr-0.5" />Not Serviceable</Badge>
                    )}
                    {selected.is_ap_ts && <Badge className="text-[10px] bg-blue-100 text-blue-700">AP/TS Route</Badge>}
                    {selected.is_letter && <Badge className="text-[10px] bg-purple-100 text-purple-700">Letter ≤50g</Badge>}
                    {(selected.order_type === "B2B") && <Badge className="text-[10px] bg-yellow-100 text-yellow-700">B2B 20kg Min</Badge>}
                  </div>
                </div>
              )}

              {/* SUB-SECTION B: Consolidation Alert */}
              {selected && selected.consolidation_count > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="consolidation-alert">
                  <div className="flex items-center gap-2 text-amber-800 text-sm font-medium mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    {selected.consolidation_count} other shipment{selected.consolidation_count > 1 ? "s" : ""} also going to {selected.dest_city} today
                  </div>
                  <p className="text-xs text-amber-600">Consider consolidating for better rates.</p>
                </div>
              )}

              {/* SUB-SECTION C: Connection Options */}
              <div data-testid="connection-options">
                <h3 className="text-sm font-bold text-slate-800 mb-1">Recommended Connections</h3>
                <p className="text-xs text-slate-500 mb-3">Based on your SOP — select to assign</p>
                {optionsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="ml-2 text-xs text-slate-400">Calculating options...</span></div>
                ) : options.length === 0 ? (
                  <div className="text-center py-8 text-xs text-slate-400">No routing options available</div>
                ) : (
                  <div className="space-y-3">
                    {options.map(opt => <OptionCard key={opt.carrier} opt={opt} onAssign={handleAssignClick} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Assign Confirm Dialog (Recommended) ─── */}
        <Dialog open={!!assignOpt} onOpenChange={() => setAssignOpt(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirm Assignment</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              Assign to <span className="font-bold">{assignOpt?.display}</span>?
            </p>
            {assignOpt?.carrier?.startsWith("OWN") ? (
              <p className="text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded">AWB will be auto-generated for own network.</p>
            ) : assignOpt?.carrier?.startsWith("DELHIVERY") ? (
              <p className="text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded">AWB will be auto-fetched from Delhivery. If unavailable, this will stay booked for manual manifestation later.</p>
            ) : (
              <div>
                <label className="text-xs font-medium text-slate-600">Enter Carrier AWB Number <span className="text-red-500">*</span></label>
                <Input value={carrierAwb} onChange={e => setCarrierAwb(e.target.value)} placeholder="e.g. DEL1234567890" className="mt-1" data-testid="assign-awb-input" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpt(null)}>Cancel</Button>
              <Button onClick={confirmAssign} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-assign-btn">Confirm Assignment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Override Modal ─── */}
        <Dialog open={!!overrideOpt} onOpenChange={() => setOverrideOpt(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Override Recommended Connection</DialogTitle></DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-700">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              SOP recommends <strong>{options.find(o => o.is_recommended)?.display || "—"}</strong>.
              You are choosing <strong>{overrideOpt?.display}</strong>.
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-2">Why are you overriding? <span className="text-red-500">*</span></label>
              <div className="space-y-1.5">
                {OVERRIDE_REASONS.map(r => (
                  <label key={r} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="override_reason" checked={overrideReason === r} onChange={() => setOverrideReason(r)} className="accent-indigo-600" />
                    <span className="text-sm text-slate-700">{r}</span>
                  </label>
                ))}
              </div>
              {overrideReason === "Other" && (
                <Textarea placeholder="Describe the reason..." value={overrideText} onChange={e => setOverrideText(e.target.value)} rows={2} className="mt-2" data-testid="override-text" />
              )}
            </div>
            {!overrideOpt?.carrier?.startsWith("OWN") && (
              <div>
                <label className="text-xs font-medium text-slate-600">Enter Carrier AWB Number <span className="text-red-500">*</span></label>
                <Input value={overrideAwb} onChange={e => setOverrideAwb(e.target.value)} placeholder="e.g. BLU9876543210" className="mt-1" data-testid="override-awb-input" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOverrideOpt(null)}>Cancel</Button>
              <Button onClick={confirmOverride} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-override-btn">Confirm Override & Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
